import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useTranslation } from "react-i18next";
import { getMyStores } from "../../api/storeService";
import { getMyProducts, bulkDiscountProducts } from "../../api/productService";
import { useLanguage } from "../../context/LanguageContext";
import { pickLocalizedName } from "../../utils/localizedName";
import { EmptyState } from "../Admin/analyticsShared";

// Numeric values must match the backend's BulkDiscountRequest enums.
const ACTION_APPLY = 1;
const ACTION_REMOVE = 2;
const TARGET_ALL = 1;
const TARGET_CATEGORIES = 2;
const TARGET_PRODUCTS = 3;
const VALUE_PERCENTAGE = 1;
const VALUE_AMOUNT_OFF = 2;
const MODE_ONLY_WITHOUT = 1;
const MODE_REPLACE_ALL = 2;
const MODE_ONLY_WITH = 3;

const PERCENT_PRESETS = [10, 15, 20, 25, 30, 50];
const EXISTING_MODE_OPTIONS = [
  { value: MODE_ONLY_WITHOUT, labelKey: "existing.onlyWithout", hintKey: "existing.onlyWithoutHint" },
  { value: MODE_REPLACE_ALL, labelKey: "existing.replaceAll", hintKey: "existing.replaceAllHint" },
  { value: MODE_ONLY_WITH, labelKey: "existing.onlyWith", hintKey: "existing.onlyWithHint" },
];

const UNCATEGORIZED_ID = "none";

function hasDiscount(entity) {
  return (entity.discountType ?? 0) !== 0;
}

// Mirrors the backend's per-product/variant decision in ProductsController.BulkDiscount, so
// the preview count matches what the server will actually change.
function willChange(entity, { action, existingMode, valueType, value }) {
  const discounted = hasDiscount(entity);
  if (action === ACTION_REMOVE) return discounted;
  if (existingMode === MODE_ONLY_WITHOUT && discounted) return false;
  if (existingMode === MODE_ONLY_WITH && !discounted) return false;
  if (valueType === VALUE_AMOUNT_OFF && !(value < entity.price)) return false;
  return true;
}

function discountedPrice(price, valueType, value) {
  return valueType === VALUE_PERCENTAGE ? price * (1 - value / 100) : price - value;
}

// <input type="date"> works with local "YYYY-MM-DD" strings.
function toDateInput(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function fromDateInput(text, endOfDay) {
  if (!text) return null;
  const [y, m, d] = text.split("-").map(Number);
  return endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 0) : new Date(y, m - 1, d, 0, 0, 0, 0);
}

function money(amount) {
  return `₪${Number(amount ?? 0).toFixed(2)}`;
}

function SectionCard({ title, children }) {
  return (
    <Card sx={{ p: 2.5 }}>
      {title && (
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {title}
        </Typography>
      )}
      {children}
    </Card>
  );
}

function PriceTag({ product, t }) {
  if (product.discountPrice != null) {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography variant="caption" color="text.disabled" sx={{ textDecoration: "line-through" }}>
          {money(product.price)}
        </Typography>
        <Typography variant="caption" color="error.main" fontWeight={700}>
          {money(product.discountPrice)}
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography variant="caption" color="text.secondary">
        {money(product.price)}
      </Typography>
      {product.scheduledDiscountPrice != null && (
        <Chip size="small" color="info" variant="outlined" label={t("boProductDiscounts:scheduledTag")} sx={{ height: 18, fontSize: 11 }} />
      )}
    </Stack>
  );
}

export default function ProductDiscounts() {
  const { t } = useTranslation(["boProductDiscounts", "boProducts", "boStores", "boShared", "common"]);
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(() => Number(searchParams.get("storeId")) || null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [action, setAction] = useState(ACTION_APPLY);
  const [target, setTarget] = useState(TARGET_ALL);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [valueType, setValueType] = useState(VALUE_PERCENTAGE);
  const [valueText, setValueText] = useState("");
  const [existingMode, setExistingMode] = useState(MODE_ONLY_WITHOUT);
  const [includeVariants, setIncludeVariants] = useState(true);
  // Empty start = starts immediately; empty end = runs until removed.
  const [startText, setStartText] = useState("");
  const [endText, setEndText] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let active = true;
    getMyStores()
      .then((data) => {
        if (!active) return;
        setStores(data);
        setStoreId((current) => (data.some((s) => s.storeId === current) ? current : data[0]?.storeId ?? null));
        if (data.length === 0) setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoadError(t("boStores:loadError"));
        setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function loadProducts() {
    if (!storeId) return;
    setLoading(true);
    setLoadError("");
    getMyProducts(storeId)
      .then(setProducts)
      .catch(() => setLoadError(t("boProductDiscounts:loadError")))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!storeId) return;
    setSearchParams({ storeId: String(storeId) }, { replace: true });
    setSelectedCategoryIds([]);
    setSelectedProductIds([]);
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const categories = useMemo(() => {
    const groups = new Map();
    products.forEach((p) => {
      const id = p.categoryId ?? UNCATEGORIZED_ID;
      if (!groups.has(id)) {
        groups.set(id, {
          id,
          name: p.categoryName
            ? pickLocalizedName(language, p.categoryName, p.categoryNameArabic, p.categoryNameHebrew)
            : t("boProductDiscounts:uncategorized"),
          count: 0,
        });
      }
      groups.get(id).count += 1;
    });
    return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, language]);

  const searchedProducts = useMemo(() => {
    const query = productSearch.trim().toLowerCase();
    if (!query) return products;
    return products.filter((p) => p.name?.toLowerCase().includes(query));
  }, [products, productSearch]);

  const targetProducts = useMemo(() => {
    if (target === TARGET_CATEGORIES) {
      return products.filter((p) => selectedCategoryIds.includes(p.categoryId ?? UNCATEGORIZED_ID));
    }
    if (target === TARGET_PRODUCTS) {
      return products.filter((p) => selectedProductIds.includes(p.productId));
    }
    return products;
  }, [products, target, selectedCategoryIds, selectedProductIds]);

  const value = parseFloat(valueText);
  const isValueValid = action === ACTION_REMOVE || (valueType === VALUE_PERCENTAGE ? value > 0 && value < 100 : value > 0);

  const affectedProducts = useMemo(() => {
    if (!isValueValid) return [];
    const options = { action, existingMode, valueType, value };
    return targetProducts.filter(
      (p) => willChange(p, options) || (includeVariants && (p.variants ?? []).some((v) => willChange(v, options))),
    );
  }, [targetProducts, action, existingMode, valueType, value, includeVariants, isValueValid]);

  const startDate = fromDateInput(startText, false);
  const endDate = fromDateInput(endText, true);

  let scheduleError = "";
  if (action === ACTION_APPLY && endDate) {
    if (endDate <= new Date()) scheduleError = t("boProductDiscounts:errors.endInPast");
    else if (startDate && endDate <= startDate) scheduleError = t("boProductDiscounts:errors.endBeforeStart");
  }

  const submitBlockReason = (() => {
    if (target === TARGET_CATEGORIES && selectedCategoryIds.length === 0) return t("boProductDiscounts:errors.noCategories");
    if (target === TARGET_PRODUCTS && selectedProductIds.length === 0) return t("boProductDiscounts:errors.noProducts");
    if (!isValueValid) {
      return valueType === VALUE_PERCENTAGE
        ? t("boProductDiscounts:errors.percentInvalid")
        : t("boProductDiscounts:errors.amountInvalid");
    }
    if (scheduleError) return scheduleError;
    if (affectedProducts.length === 0) return t("boProductDiscounts:errors.nothingToChange");
    return "";
  })();

  const toggleId = (setter) => (id) =>
    setter((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  const toggleCategory = toggleId(setSelectedCategoryIds);
  const toggleProduct = toggleId(setSelectedProductIds);

  const allSearchedSelected =
    searchedProducts.length > 0 && searchedProducts.every((p) => selectedProductIds.includes(p.productId));
  const toggleSelectAllSearched = () => {
    const ids = searchedProducts.map((p) => p.productId);
    setSelectedProductIds((current) =>
      allSearchedSelected ? current.filter((id) => !ids.includes(id)) : Array.from(new Set([...current, ...ids])),
    );
  };

  async function submit() {
    setSubmitting(true);
    setSubmitError("");
    try {
      const realCategoryIds = selectedCategoryIds.filter((id) => id !== UNCATEGORIZED_ID);
      const data = await bulkDiscountProducts({
        storeId,
        target,
        categoryIds: target === TARGET_CATEGORIES ? realCategoryIds : null,
        productIds: target === TARGET_PRODUCTS ? selectedProductIds : null,
        action,
        valueType,
        value: action === ACTION_APPLY ? value : 0,
        existingMode,
        includeVariants,
        startsAt: action === ACTION_APPLY && startDate ? startDate.toISOString() : null,
        endsAt: action === ACTION_APPLY && endDate ? endDate.toISOString() : null,
      });
      setConfirmOpen(false);
      setResult(data);
      loadProducts();
    } catch (err) {
      setConfirmOpen(false);
      setSubmitError(typeof err.response?.data === "string" ? err.response.data : t("boProductDiscounts:errors.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  const count = affectedProducts.length;
  const confirmMessage =
    action === ACTION_REMOVE
      ? t("boProductDiscounts:confirmRemove", { count })
      : t("boProductDiscounts:confirmApply", {
          count,
          value: valueType === VALUE_PERCENTAGE ? `${value}%` : `₪${value}`,
        });

  const previewSamples = action === ACTION_APPLY && isValueValid ? affectedProducts.slice(0, 3) : [];
  const today = toDateInput(new Date());

  const choiceChip = (selected, label, onClick, key) => (
    <Chip
      key={key}
      label={label}
      onClick={onClick}
      disabled={submitting}
      color={selected ? "primary" : "default"}
      variant={selected ? "filled" : "outlined"}
    />
  );

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate(`/business-owner/products${storeId ? `?storeId=${storeId}` : ""}`)}
        sx={{ mb: 0.5 }}
      >
        {t("boProductDiscounts:backToProducts")}
      </Button>

      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 3 }}>
        <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
          <LocalOfferOutlinedIcon />
        </Avatar>
        <Box>
          <Typography variant="h4">{t("boProductDiscounts:title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("boProductDiscounts:subtitle")}
          </Typography>
        </Box>
      </Stack>

      {stores.length > 1 && (
        <FormControl size="small" sx={{ minWidth: 220, mb: 2.5 }}>
          <InputLabel>{t("boShared:storeFallback")}</InputLabel>
          <Select
            label={t("boShared:storeFallback")}
            value={storeId ?? ""}
            onChange={(e) => setStoreId(e.target.value)}
            disabled={submitting}
          >
            {stores.map((s) => (
              <MenuItem key={s.storeId} value={s.storeId}>
                {s.storeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError("")}>
          {submitError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert severity="error">{loadError}</Alert>
      ) : !storeId ? (
        <EmptyState icon={Inventory2OutlinedIcon} text={t("boProducts:noStores")} />
      ) : products.length === 0 ? (
        <EmptyState icon={LocalOfferOutlinedIcon} text={t("boProductDiscounts:noProducts")} />
      ) : (
        <Stack spacing={2}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            color={action === ACTION_REMOVE ? "error" : "primary"}
            value={action}
            onChange={(e, next) => next && setAction(next)}
            disabled={submitting}
          >
            <ToggleButton value={ACTION_APPLY} sx={{ gap: 1, textTransform: "none", fontWeight: 600 }}>
              <LocalOfferOutlinedIcon fontSize="small" />
              {t("boProductDiscounts:actionApply")}
            </ToggleButton>
            <ToggleButton value={ACTION_REMOVE} sx={{ gap: 1, textTransform: "none", fontWeight: 600 }}>
              <HighlightOffRoundedIcon fontSize="small" />
              {t("boProductDiscounts:actionRemove")}
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Which products */}
          <SectionCard title={t("boProductDiscounts:targetTitle")}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {choiceChip(target === TARGET_ALL, t("boProductDiscounts:targetAll", { count: products.length }), () => setTarget(TARGET_ALL), "all")}
              {choiceChip(target === TARGET_CATEGORIES, t("boProductDiscounts:targetCategories"), () => setTarget(TARGET_CATEGORIES), "cat")}
              {choiceChip(target === TARGET_PRODUCTS, t("boProductDiscounts:targetProducts"), () => setTarget(TARGET_PRODUCTS), "prod")}
            </Stack>

            {target === TARGET_CATEGORIES && (
              <Stack sx={{ mt: 1.5 }}>
                {categories.map((category) => (
                  <FormControlLabel
                    key={String(category.id)}
                    disabled={submitting}
                    control={
                      <Checkbox checked={selectedCategoryIds.includes(category.id)} onChange={() => toggleCategory(category.id)} />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{category.name}</Typography>
                        <Chip size="small" variant="outlined" label={category.count} sx={{ height: 20 }} />
                      </Stack>
                    }
                  />
                ))}
              </Stack>
            )}

            {target === TARGET_PRODUCTS && (
              <Box sx={{ mt: 1.5 }}>
                <TextField
                  placeholder={t("boProducts:searchPlaceholder")}
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  size="small"
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                      endAdornment: productSearch && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setProductSearch("")}>
                            <CloseRoundedIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("boProductDiscounts:selectedCount", { count: selectedProductIds.length })}
                  </Typography>
                  <Button size="small" onClick={toggleSelectAllSearched} disabled={submitting}>
                    {allSearchedSelected ? t("boProductDiscounts:clearSelection") : t("boProductDiscounts:selectAll")}
                  </Button>
                </Stack>
                <Box sx={{ maxHeight: 420, overflowY: "auto", borderTop: 1, borderColor: "divider" }}>
                  {searchedProducts.map((p) => {
                    const image = p.images?.find((i) => i.isMain)?.imageUrl || p.images?.[0]?.imageUrl || undefined;
                    const checked = selectedProductIds.includes(p.productId);
                    return (
                      <Stack
                        key={p.productId}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        onClick={() => !submitting && toggleProduct(p.productId)}
                        sx={{
                          py: 0.75,
                          px: 0.5,
                          cursor: "pointer",
                          borderBottom: 1,
                          borderColor: "divider",
                          "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) },
                        }}
                      >
                        <Checkbox checked={checked} disabled={submitting} sx={{ p: 0.5 }} />
                        <Avatar src={image} variant="rounded" sx={{ width: 40, height: 40, bgcolor: "action.hover" }}>
                          <Inventory2OutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        </Avatar>
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography variant="body2" noWrap>
                            {p.name}
                          </Typography>
                          <PriceTag product={p} t={t} />
                        </Box>
                      </Stack>
                    );
                  })}
                </Box>
              </Box>
            )}
          </SectionCard>

          {action === ACTION_APPLY && (
            <>
              {/* Discount value */}
              <SectionCard title={t("boProductDiscounts:valueTitle")}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                  {choiceChip(valueType === VALUE_PERCENTAGE, t("boProductDiscounts:valuePercentage"), () => setValueType(VALUE_PERCENTAGE), "pct")}
                  {choiceChip(valueType === VALUE_AMOUNT_OFF, t("boProductDiscounts:valueAmountOff"), () => setValueType(VALUE_AMOUNT_OFF), "amt")}
                </Stack>
                <TextField
                  type="number"
                  size="small"
                  value={valueText}
                  onChange={(e) => setValueText(e.target.value)}
                  disabled={submitting}
                  placeholder={
                    valueType === VALUE_PERCENTAGE
                      ? t("boProductDiscounts:percentPlaceholder")
                      : t("boProductDiscounts:amountPlaceholder")
                  }
                  sx={{ maxWidth: 240 }}
                  slotProps={{
                    htmlInput: { min: 0, step: "any" },
                    input: {
                      endAdornment: <InputAdornment position="end">{valueType === VALUE_PERCENTAGE ? "%" : "₪"}</InputAdornment>,
                    },
                  }}
                />
                {valueType === VALUE_PERCENTAGE ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                    {PERCENT_PRESETS.map((preset) =>
                      choiceChip(String(preset) === valueText, `${preset}%`, () => setValueText(String(preset)), preset),
                    )}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    {t("boProductDiscounts:amountHint")}
                  </Typography>
                )}
              </SectionCard>

              {/* Products that already have a discount */}
              <SectionCard title={t("boProductDiscounts:existingTitle")}>
                <Stack spacing={1}>
                  {EXISTING_MODE_OPTIONS.map((option) => {
                    const selected = existingMode === option.value;
                    return (
                      <Stack
                        key={option.value}
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        onClick={() => !submitting && setExistingMode(option.value)}
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          cursor: "pointer",
                          border: 1,
                          borderColor: selected ? "primary.main" : "divider",
                          bgcolor: (theme) => (selected ? alpha(theme.palette.primary.main, 0.06) : "transparent"),
                        }}
                      >
                        <Radio checked={selected} disabled={submitting} size="small" sx={{ p: 0.5 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {t(`boProductDiscounts:${option.labelKey}`)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t(`boProductDiscounts:${option.hintKey}`)}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              </SectionCard>

              {/* Schedule */}
              <SectionCard title={t("boProductDiscounts:scheduleTitle")}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    type="date"
                    size="small"
                    label={t("boProductDiscounts:startsLabel")}
                    value={startText}
                    onChange={(e) => setStartText(e.target.value === today ? "" : e.target.value)}
                    disabled={submitting}
                    helperText={startText ? " " : t("boProductDiscounts:startsNow")}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today } }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    label={t("boProductDiscounts:endsLabel")}
                    value={endText}
                    onChange={(e) => setEndText(e.target.value)}
                    disabled={submitting}
                    helperText={endText ? " " : t("boProductDiscounts:noEnd")}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: startText || today } }}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  {t("boProductDiscounts:scheduleHint")}
                </Typography>
                {scheduleError && (
                  <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.5 }}>
                    {scheduleError}
                  </Typography>
                )}
              </SectionCard>
            </>
          )}

          <SectionCard>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {t("boProductDiscounts:includeVariants")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("boProductDiscounts:includeVariantsHint")}
                </Typography>
              </Box>
              <Switch checked={includeVariants} onChange={(e) => setIncludeVariants(e.target.checked)} disabled={submitting} />
            </Stack>
          </SectionCard>

          {/* Summary */}
          <Card sx={{ p: 2.5, bgcolor: "action.hover" }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t("boProductDiscounts:summary", { count })}
            </Typography>
            {previewSamples.map((p) => (
              <Stack key={p.productId} direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.75 }}>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                  {p.name}
                </Typography>
                <Typography variant="body2">
                  {willChange(p, { action, existingMode, valueType, value })
                    ? `${money(p.price)} → ${money(discountedPrice(p.price, valueType, value))}`
                    : t("boProductDiscounts:variantsOnly")}
                </Typography>
              </Stack>
            ))}
            {submitBlockReason && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                {submitBlockReason}
              </Typography>
            )}
            <Button
              variant="contained"
              color={action === ACTION_REMOVE ? "error" : "primary"}
              size="large"
              fullWidth
              sx={{ mt: 2 }}
              disabled={submitting || Boolean(submitBlockReason)}
              onClick={() => setConfirmOpen(true)}
            >
              {action === ACTION_REMOVE
                ? t("boProductDiscounts:removeButton", { count })
                : t("boProductDiscounts:applyButton", { count })}
            </Button>
          </Card>
        </Stack>
      )}

      <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("boProductDiscounts:confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting} color="inherit">
            {t("common:cancel")}
          </Button>
          <Button
            variant="contained"
            color={action === ACTION_REMOVE ? "error" : "primary"}
            onClick={submit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t("boProductDiscounts:confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(result)} onClose={() => setResult(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("boProductDiscounts:successTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {result &&
              t("boProductDiscounts:successMessage", { products: result.updatedProducts, variants: result.updatedVariants })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResult(null)}>{t("common:ok")}</Button>
          <Button
            variant="contained"
            onClick={() => navigate(`/business-owner/products${storeId ? `?storeId=${storeId}` : ""}`)}
          >
            {t("boProductDiscounts:backToProducts")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
